/**
 * Fiduciary Validation
 *
 * Validates the canonical model before rendering and document generation.
 * Catches structural errors (missing tiers, invalid pairs, role/package
 * mismatches) and warnings (unusual but permissible configurations).
 *
 * This is the explicit validation stage in the pipeline:
 *   import JSON → normalize → **validate** → attorney summary → render
 */

import {
  FIDUCIARY_ROLES,
  isRoleAllowed,
  getMaxSuccessorDepth,
  allowsPair,
  getRoleCondition,
  isMarriedPackage,
  getPackageRules,
} from './packageCapabilities.js';
import { getSuccessorCount } from './fiduciarySchema.js';

// ---------------------------------------------------------------------------
// Main Validation
// ---------------------------------------------------------------------------

/**
 * Validate a canonical model.
 * @param {object} model - Canonical model from normalizeFromImport/normalizeFromUI
 * @returns {{ valid: boolean, errors: object[], warnings: object[] }}
 *   errors: blocking issues that prevent generation
 *   warnings: advisory issues the attorney should review
 */
export function validateCanonicalModel(model) {
  const errors = [];
  const warnings = [];

  const packageType = model.packageConfig?.package_type;
  const hasMinorChildren = model.packageConfig?.has_minor_children;

  // 1. Package type must be set
  if (!packageType) {
    errors.push({ field: 'packageConfig.package_type', message: 'Package type is required.' });
  }

  // 2. Client must have a name
  if (!model.client?.full_name?.trim()) {
    errors.push({ field: 'client.full_name', message: 'Client full name is required.' });
  }

  // 3. Married packages require spouse
  if (packageType && isMarriedPackage(packageType)) {
    if (!model.spouse?.full_name?.trim()) {
      errors.push({ field: 'spouse.full_name', message: `Spouse name is required for ${packageType}.` });
    }
  }

  // 4. has_minor_children must be explicitly set
  if (hasMinorChildren === null || hasMinorChildren === undefined) {
    const children = model.children || [];
    if (children.length > 0 && children.some(c => c.is_minor === null || c.is_minor === undefined)) {
      errors.push({
        field: 'packageConfig.has_minor_children',
        message: 'Cannot determine if client has minor children. Set is_minor on each child.',
      });
    }
  }

  // 5. Validate each role assignment
  if (packageType) {
    for (const [roleId, assignment] of Object.entries(model.roleAssignments || {})) {
      validateRole(roleId, assignment, model, errors, warnings);
    }
  }

  // 6. Validate age thresholds
  validateAgeThresholds(model, errors, warnings);

  // 7. Validate survivorship
  validateSurvivorship(model, errors, warnings);

  // 8. Check for missing required roles
  if (packageType) {
    validateRequiredRoles(model, errors, warnings);
  }

  // 9. Cross-person checks
  validatePeopleConsistency(model, warnings);

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Role Validation
// ---------------------------------------------------------------------------

function validateRole(roleId, assignment, model, errors, warnings) {
  const packageType = model.packageConfig.package_type;
  const hasMinorChildren = model.packageConfig.has_minor_children;
  const tiers = assignment.tiers || [];

  // Role not allowed for this package
  if (!isRoleAllowed(roleId, packageType) && tiers.length > 0) {
    warnings.push({
      field: `roleAssignments.${roleId}`,
      message: `${FIDUCIARY_ROLES[roleId]?.label || roleId} is not typically used in ${packageType}. Appointments will be ignored.`,
    });
    return;
  }

  // Conditional role present when condition not met
  const condition = getRoleCondition(roleId);
  if (condition === 'has_minor_children' && hasMinorChildren === false && tiers.length > 0) {
    warnings.push({
      field: `roleAssignments.${roleId}`,
      message: `${FIDUCIARY_ROLES[roleId]?.label} is assigned but client has no minor children. These appointments will not appear in the document.`,
    });
  }

  // Check each tier
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];

    // Empty tier (should have been filtered, but defensive)
    if (!tier.people || tier.people.length === 0) {
      errors.push({
        field: `roleAssignments.${roleId}.tiers[${i}]`,
        message: `${FIDUCIARY_ROLES[roleId]?.label} tier ${i + 1} has no people assigned.`,
      });
      continue;
    }

    // Pair validation
    if (tier.people.length > 2) {
      errors.push({
        field: `roleAssignments.${roleId}.tiers[${i}]`,
        message: `${FIDUCIARY_ROLES[roleId]?.label} tier ${i + 1} has ${tier.people.length} people. Maximum is 2 (co-fiduciary pair).`,
      });
    }

    if (tier.people.length === 2 && !allowsPair(roleId)) {
      errors.push({
        field: `roleAssignments.${roleId}.tiers[${i}]`,
        message: `${FIDUCIARY_ROLES[roleId]?.label} does not allow co-fiduciary (paired) appointments.`,
      });
    }

    // Verify all person IDs exist in people registry
    for (const personId of tier.people) {
      if (!model.people[personId]) {
        errors.push({
          field: `roleAssignments.${roleId}.tiers[${i}]`,
          message: `Person ID "${personId}" referenced in ${FIDUCIARY_ROLES[roleId]?.label} not found in people registry.`,
        });
      }
    }
  }

  // Gap check: successor tier exists but predecessor is missing
  const roleDef = FIDUCIARY_ROLES[roleId];
  if (roleDef && tiers.length > 1) {
    for (let i = 1; i < tiers.length; i++) {
      if (tiers[i].people.length > 0 && tiers[i - 1].people.length === 0) {
        errors.push({
          field: `roleAssignments.${roleId}.tiers[${i}]`,
          message: `${roleDef.label} tier ${i + 1} is assigned but tier ${i} is empty. Cannot have gaps in succession.`,
        });
      }
    }
  }

  // Exceeds max successor depth
  const successorCount = getSuccessorCount(model, roleId);
  const maxDepth = getMaxSuccessorDepth(roleId);
  if (successorCount > maxDepth) {
    warnings.push({
      field: `roleAssignments.${roleId}`,
      message: `${roleDef?.label} has ${successorCount} successor tiers but maximum is ${maxDepth}. Extra tiers will be ignored.`,
    });
  }
}

// ---------------------------------------------------------------------------
// Age Threshold Validation
// ---------------------------------------------------------------------------

function validateAgeThresholds(model, errors, warnings) {
  const tc = model.trustConfig || {};

  const cotrustAge = tc.beneficiary_cotrust_age;
  const soleAge = tc.beneficiary_sole_trust_age;
  const protectorAge = tc.trust_protector_age;
  const hasMinorChildren = model.packageConfig?.has_minor_children === true;
  const potAge = hasMinorChildren ? tc.pot_trust_termination_age : null;

  // Co-trustee age must be less than sole trustee age
  if (cotrustAge != null && soleAge != null && cotrustAge >= soleAge) {
    errors.push({
      field: 'trustConfig',
      message: `Co-trustee age (${cotrustAge}) must be less than sole trustee age (${soleAge}).`,
    });
  }

  // Reasonable range checks (advisory)
  for (const [name, value] of [
    ['Co-trustee age', cotrustAge],
    ['Sole trustee age', soleAge],
    ['Trust protector age', protectorAge],
    ['Pot trust termination age', potAge],
  ]) {
    if (value != null && (value < 18 || value > 65)) {
      warnings.push({
        field: 'trustConfig',
        message: `${name} is ${value}. Typical range is 18-65. Please confirm this is correct.`,
      });
    }
  }

  // Pot trust age is only meaningful when has_minor_children is true. Adult-only
  // plans intentionally suppress this setting without a user-visible warning.
}

// ---------------------------------------------------------------------------
// Survivorship Validation
// ---------------------------------------------------------------------------

function validateSurvivorship(model, errors, warnings) {
  const direction = model.packageConfig?.survivorship_direction;
  const packageType = model.packageConfig?.package_type;

  if (direction && direction !== 'spouse_survives' && direction !== 'client_survives') {
    errors.push({
      field: 'packageConfig.survivorship_direction',
      message: `Invalid survivorship direction: "${direction}". Must be "spouse_survives" or "client_survives".`,
    });
  }

  // Survivorship only applies to married packages
  if (direction && packageType && !isMarriedPackage(packageType)) {
    warnings.push({
      field: 'packageConfig.survivorship_direction',
      message: `Survivorship direction is set but ${packageType} is not a married package. This setting will be ignored.`,
    });
  }
}

// ---------------------------------------------------------------------------
// Required Roles Validation
// ---------------------------------------------------------------------------

function validateRequiredRoles(model, errors, warnings) {
  const packageType = model.packageConfig.package_type;
  const hasMinorChildren = model.packageConfig.has_minor_children;
  const rules = getPackageRules(packageType);
  if (!rules) return;

  // Roles that should have at least one tier assigned
  const requiredRoles = ['executor', 'trustee', 'trust_protector', 'financial_agent', 'healthcare_agent'];

  for (const roleId of requiredRoles) {
    if (!isRoleAllowed(roleId, packageType)) continue;

    const tiers = model.roleAssignments[roleId]?.tiers || [];
    if (tiers.length === 0 || tiers[0].people.length === 0) {
      errors.push({
        field: `roleAssignments.${roleId}`,
        message: `${FIDUCIARY_ROLES[roleId]?.label} is required for ${packageType} but no one is assigned.`,
      });
    }
  }

  // Guardian required only if minor children
  if (hasMinorChildren === true) {
    const guardianTiers = model.roleAssignments.guardian?.tiers || [];
    if (guardianTiers.length === 0 || guardianTiers[0].people.length === 0) {
      errors.push({
        field: 'roleAssignments.guardian',
        message: 'Guardian is required when client has minor children.',
      });
    }
  }

  // Healthcare agents need contact info
  const hcTiers = model.roleAssignments.healthcare_agent?.tiers || [];
  for (let i = 0; i < hcTiers.length; i++) {
    for (const personId of hcTiers[i].people) {
      const person = model.people[personId];
      if (person) {
        if (!person.street_address) {
          errors.push({
            field: `people.${personId}.street_address`,
            message: `Healthcare agent ${person.name} requires a street address.`,
          });
        }
        if (!person.city_state_zip) {
          errors.push({
            field: `people.${personId}.city_state_zip`,
            message: `Healthcare agent ${person.name} requires city, state, and zip.`,
          });
        }
        if (!person.telephone) {
          errors.push({
            field: `people.${personId}.telephone`,
            message: `Healthcare agent ${person.name} requires a phone number.`,
          });
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// People Consistency Checks
// ---------------------------------------------------------------------------

function validatePeopleConsistency(model, warnings) {
  // Check if client and spouse are the same person
  if (model.client?.full_name && model.spouse?.full_name) {
    if (model.client.full_name.trim().toLowerCase() === model.spouse.full_name.trim().toLowerCase()) {
      warnings.push({
        field: 'client',
        message: 'Client and spouse have the same name. Please confirm this is correct.',
      });
    }
  }

  // Check for person with no relationship set (except estate protector which doesn't require one)
  for (const [personId, person] of Object.entries(model.people || {})) {
    if (!person.relationship) {
      // Only warn if this person is in a role that requires relationship
      const isInRelRequiredRole = Object.entries(model.roleAssignments || {}).some(
        ([roleId, assignment]) => {
          if (roleId === 'estate_protector') return false;
          return assignment.tiers.some(t => t.people.includes(personId));
        }
      );
      if (isInRelRequiredRole) {
        warnings.push({
          field: `people.${personId}`,
          message: `${person.name} has no relationship to client specified.`,
        });
      }
    }
  }
}
